import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { PageHeader } from "@/components/app/Layout";
import { Package, Scale, Warehouse, Wallet, Clock, Factory, TrendingDown } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";

const Stat = ({ label, value, unit = "", icon: Icon, tone, testid }) => (
  <div data-testid={testid} className="card-flat p-4 md:p-5">
    <div className="flex items-start justify-between">
      <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`h-9 w-9 flex items-center justify-center rounded-sm ${tone}`}>
        <Icon size={16} className="text-white"/>
      </div>
    </div>
    <div className="mt-3 md:mt-4 font-mono text-lg md:text-2xl tabular-nums text-slate-900">
      {value}{unit && <span className="text-sm text-slate-500 ml-1">{unit}</span>}
    </div>
  </div>
);

export default function DashboardArang() {
  const [d, setD] = useState(null);
  const [trend, setTrend] = useState([]);
  useEffect(() => {
    api.get("/dashboard/arang").then(r => setD(r.data));
    api.get("/dashboard/arang/trend").then(r => setTrend(r.data));
  }, []);

  const kg = (v) => `${Number(v || 0).toLocaleString("id-ID", { maximumFractionDigits: 2 })} Kg`;

  const chartData = trend.map(t => ({
    name: t.bulan.split(" ")[0].slice(0, 3),
    produksi: t.produksi_kg,
    penjualan: t.penjualan_kg,
    stok: t.stok_kumulatif_kg,
  }));

  return (<>
    <PageHeader title="Dashboard Bisnis Arang" subtitle="Ringkasan produksi, penjualan, stok, dan piutang."/>
    <div className="p-4 md:p-8 space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
        <Stat testid="a-stat-produksi" label="Total Produksi" value={kg(d?.total_produksi_kg)} icon={Factory} tone="bg-[color:var(--brand)]"/>
        <Stat testid="a-stat-sales" label="Total Terjual" value={kg(d?.total_sales_kg)} icon={Scale} tone="bg-emerald-600"/>
        <Stat testid="a-stat-stok" label="Sisa Stok" value={kg(d?.sisa_stok_kg)} icon={Warehouse} tone="bg-indigo-600"/>
        <Stat testid="a-stat-hpp" label="HPP Rata-rata" value={rupiah(d?.hpp_rata)} unit="/Kg" icon={Package} tone="bg-slate-700"/>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5">
        <Stat testid="a-stat-laba" label="Laba Bersih" value={rupiah(d?.laba_bersih)} icon={Wallet} tone="bg-emerald-700"/>
        <Stat testid="a-stat-piutang" label="Piutang" value={rupiah(d?.piutang)} icon={Clock} tone="bg-amber-600"/>
        <Stat testid="a-stat-biaya" label="Total Biaya Produksi" value={rupiah(d?.total_biaya_produksi)} icon={TrendingDown} tone="bg-rose-600"/>
      </div>

      <div className="card-flat p-4 md:p-6" data-testid="arang-trend-chart">
        <h3 className="text-base md:text-lg font-semibold text-slate-900">Tren Produksi vs Penjualan (12 Bulan)</h3>
        <p className="text-xs md:text-sm text-slate-500 mb-3">Bar = Kg produksi & penjualan bulanan. Line = Sisa stok kumulatif.</p>
        <div style={{ width: "100%", minHeight: 320 }}>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0"/>
              <XAxis dataKey="name" stroke="#64748B" style={{ fontSize: 11 }}/>
              <YAxis stroke="#64748B" style={{ fontSize: 11 }} tickFormatter={(v)=>`${v}`}/>
              <Tooltip formatter={(v)=>`${Number(v).toLocaleString("id-ID")} Kg`} contentStyle={{ fontSize: 12, borderRadius: 4, borderColor: "#166db4" }}/>
              <Legend wrapperStyle={{ fontSize: 12 }}/>
              <Bar dataKey="produksi" name="Produksi (Kg)" fill="#166db4" radius={[3,3,0,0]}/>
              <Bar dataKey="penjualan" name="Penjualan (Kg)" fill="#10B981" radius={[3,3,0,0]}/>
              <Line type="monotone" dataKey="stok" name="Stok Kumulatif" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }}/>
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card-flat p-4 md:p-6">
        <h3 className="text-base md:text-lg font-semibold text-slate-900 mb-3">Ringkasan Cash Flow</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div className="p-3 border rounded-sm">
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Total Pemasukan</div>
            <div className="font-mono text-lg mt-1">{rupiah(d?.total_pemasukan)}</div>
          </div>
          <div className="p-3 border rounded-sm">
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Total Pengeluaran</div>
            <div className="font-mono text-lg mt-1">{rupiah(d?.total_pengeluaran)}</div>
          </div>
          <div className="p-3 border rounded-sm brand-soft">
            <div className="text-[11px] uppercase tracking-widest brand-text">Laba Bersih</div>
            <div className="font-mono text-lg mt-1 brand-text font-semibold">{rupiah(d?.laba_bersih)}</div>
          </div>
        </div>
      </div>
    </div>
  </>);
}
